import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useHref, useNavigate } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { AppShell, DimUiProvider } from '@bn-apps/dim-ui-react';
import { EntitySourceProvider } from '@bn-apps/dim-ui-entity/react';
import i18n from '@i18n/config';
import { LocaleProvider } from '@contexts/LocaleContext';
import { AuthProvider } from '@contexts/AuthContext';
import routes from '@config/routes';
import { isToolPath } from '@config/tools';
import Header from '@components/header/Header';
import Footer from '@components/footer/Footer';
import ErrorBoundary from '@components/common/ErrorBoundary';
import ScrollToTop from '@components/common/ScrollToTop';
import { SectionTabs } from '@components/common/SectionTabs';
import SessionTimeout from '@components/auth/SessionTimeout';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import AccessDenied from '@components/auth/AccessDenied';
import LandingPage from '@components/landing/LandingPage';
import HelpSection from '@components/help/HelpSection';
import HelpDetailPage from '@components/help/HelpDetailPage';
import FilesPage from '@components/files/FilesPage';
import DatasetsPage from '@components/datasets/DatasetsPage';
import MapPage from '@components/map/MapPage';
import ToolsPage from '@components/tools/ToolsPage';
import EntityPage from '@components/entity/EntityPage';
import { entitySource } from '@services/entityView.service';
import KeyFiguresPage from '@components/keyFigures/KeyFiguresPage';

const SearchPage = lazy(() => import('@components/search/SearchPage'));
const SwaggerPage = lazy(() => import('@components/swagger/SwaggerPage'));
const StatusReportsPage = lazy(() => import('@components/statusReports/StatusReportsPage'));
const StatusReportDetailPage = lazy(() => import('@components/statusReports/StatusReportDetailPage'));
const CatalogPage = lazy(() => import('@components/catalog/CatalogPage'));
const RinfStatusPage = lazy(() => import('@components/rinfStatus/RinfStatusPage'));
// OpenLayers and the topology data are only loaded when this page is opened
const TpsMappingPage = lazy(() => import('@components/tpsMapping/TpsMappingPage'));
const RailmlStatusPage = lazy(() => import('@components/railmlStatus/RailmlStatusPage'));

// Placeholder components - will be implemented in later phases
const AdminPage = (): JSX.Element => <div>Admin Page - Coming Soon</div>;

/** DIM UI's links navigate through React Router and speak the app's language. */
const Layout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const navigateTo = (to: string): void => {
    if (isToolPath(to)) window.location.assign(to);
    else navigate(to);
  };
  return (
    <DimUiProvider navigate={navigateTo} useHref={useHref} locale={i18n.language}>
      <AppShell header={<Header />} footer={<Footer />} skipLinkLabel={t('common.skipToContent')}>
        {children}
      </AppShell>
    </DimUiProvider>
  );
};

function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <LocaleProvider>
          <AuthProvider>
            <EntitySourceProvider source={entitySource}>
            <BrowserRouter>
              <ScrollToTop />
              <SessionTimeout />
              <Layout>
                  <SectionTabs />
                  {/* One boundary, so the current page stays until the next one has loaded */}
                  <Suspense fallback={null}>
                    <Routes>
                      <Route path={routes.home} element={<LandingPage />} />
                      <Route path={routes.help} element={<HelpSection />} />
                      <Route path={routes.helpDetail} element={<HelpDetailPage />} />
                      <Route
                        path={routes.files}
                        element={(
                          <ProtectedRoute>
                            <FilesPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.datasets}
                        element={(
                          <ProtectedRoute>
                            <DatasetsPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.map}
                        element={(
                          <ProtectedRoute>
                            <MapPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.tools}
                        element={(
                          <ProtectedRoute>
                            <ToolsPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.search}
                        element={(
                          <ProtectedRoute>
                            <SearchPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.admin}
                        element={(
                          <ProtectedRoute requiredAccessLevel="projectMember">
                            <AdminPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route path={routes.accessDenied} element={<AccessDenied />} />
                      <Route path={routes.forbidden} element={<AccessDenied requiredLevel="Admin or Op role" />} />
                      <Route
                        path={routes.keyFigures}
                        element={(
                          <ProtectedRoute>
                            <KeyFiguresPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.catalog}
                        element={(
                          <ProtectedRoute>
                            <CatalogPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.catalogDataset}
                        element={(
                          <ProtectedRoute>
                            <CatalogPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.rinfStatus}
                        element={(
                          <ProtectedRoute>
                            <RinfStatusPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.tpsMapping}
                        element={(
                          <ProtectedRoute>
                            <TpsMappingPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.railmlStatus}
                        element={(
                          <ProtectedRoute>
                            <RailmlStatusPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.swagger}
                        element={(
                          <ProtectedRoute>
                            <SwaggerPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route path={routes.reports} element={<Navigate to={routes.statusReports} replace />} />
                      <Route
                        path={routes.statusReports}
                        element={(
                          <ProtectedRoute>
                            <StatusReportsPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route
                        path={routes.statusReportDetail}
                        element={(
                          <ProtectedRoute>
                            <StatusReportDetailPage />
                          </ProtectedRoute>
                        )}
                      />
                      <Route path={routes.data} element={<EntityPage type="data" />} />
                      <Route path={routes.ontology} element={<EntityPage type="ontology" />} />
                      <Route path={routes.dereference} element={<EntityPage type="dereference" />} />
                    </Routes>
                  </Suspense>
              </Layout>
            </BrowserRouter>
            </EntitySourceProvider>
          </AuthProvider>
        </LocaleProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
